import { useEffect, useState } from 'react';
import { getCategories, getProducts, getCarouselSlides, getBlogPosts, getHomeBentoItems } from './services';
import type { StrapiCategory, StrapiProduct, StrapiCarouselSlide, StrapiBlogPost, StrapiHomeBentoItem } from './services';

/**
 * Hook para obtener las categorías
 * @returns Un objeto con las categorías, el estado de carga y el error
 */
export function useCategories() {
  const [categories, setCategories] = useState<StrapiCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setCategories(data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar categorías:', err);
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar categorías'));
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
}

/**
 * Hook para obtener los productos
 * @param categorySlug - Slug de la categoría (opcional)
 * @returns Un objeto con los productos, el estado de carga y el error
 */
export function useProducts(categorySlug?: string) {
  const [products, setProducts] = useState<StrapiProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await getProducts(categorySlug);
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar productos:', err);
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar productos'));
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categorySlug]);

  return { products, loading, error };
}

/**
 * Hook para obtener las diapositivas del carrusel
 * @returns Un objeto con las diapositivas, el estado de carga y el error
 */
export function useCarouselSlides() {
  const [slides, setSlides] = useState<StrapiCarouselSlide[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        setLoading(true);
        const data = await getCarouselSlides();
        setSlides(data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar diapositivas del carrusel:', err);
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar diapositivas'));
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  return { slides, loading, error };
}

/**
 * Hook para obtener el bento horizontal de la home.
 */
export function useHomeBentoItems() {
  const [items, setItems] = useState<StrapiHomeBentoItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const data = await getHomeBentoItems();
        setItems(data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar el bento de la home:', err);
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar el bento'));
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  return { items, loading, error };
}

/**
 * Hook para obtener los posts del blog
 * @param limit - Número de posts a obtener (por defecto 10)
 * @param start - Índice de inicio para paginación
 * @returns Un objeto con los posts, el estado de carga y el error
 */
export function useBlogPosts(limit: number = 10, start: number = 0) {
  const [posts, setPosts] = useState<StrapiBlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const data = await getBlogPosts(limit, start);
        setPosts(data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar posts del blog:', err);
        setError(err instanceof Error ? err : new Error('Error desconocido al cargar posts'));
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [limit, start]);

  return { posts, loading, error };
} 
